/**
 * Les credencials que fabriquen les funcions edge d'OBR-25: de què es deriva cada resum, el correu
 * opac dels comptes sintètics i el codi d'enrolament d'un aparell (pla §4.2, §4.3, §4.4).
 *
 * Aquest mòdul és PUR i només dona la FORMA i els MISSATGES; l'HMAC i l'aleatorietat són de qui crida
 * (la funció edge, amb WebCrypto, i `randomBytes` per paràmetre). Les funcions de Deno no poden importar
 * aquest paquet i en porten una còpia; un test de paritat les vigila.
 *
 * TRES CLAUS, no una (pla §4.3), i cada missatge porta el seu prefix de domini perquè mai dos usos
 * comparteixin resum encara que la clau fos la mateixa:
 *
 *   PIN_LOOKUP_KEY   — la cerca:           lookup   = HMAC(PIN_LOOKUP_KEY,  "lookup:" ‖ PIN)
 *   PIN_ACCOUNT_KEY  — els comptes:        password = HMAC(PIN_ACCOUNT_KEY, "acct:"   ‖ llavor)
 *   DEVICE_KEY       — els aparells:       etiqueta, resum del secret i resum del codi d'enrolament
 *
 * Rotar qualsevol de les tres és DESTRUCTIU i no hi ha cap camí de transició: com que el PIN no es desa, girar
 * `PIN_LOOKUP_KEY` fa que cap PIN trobi la seva fila (cal regenerar-los tots); girar `PIN_ACCOUNT_KEY` fa que cap
 * contrasenya de compte coincideixi (mateix remei); girar `DEVICE_KEY` invalida tots els testimonis (cal tornar a
 * enrolar tots els aparells). Una rotació sense destruir res exigiria acceptar dues claus alhora i reescriure el
 * lookup en el següent accés; no s'ha fet.
 */

import { PIN_ALPHABET } from './pin.js';

/** Els noms dels tres secrets de projecte (`insforge secrets add <NOM>`). */
export const PIN_SECRET_NAMES = ['PIN_LOOKUP_KEY', 'PIN_ACCOUNT_KEY', 'DEVICE_KEY'] as const;

// ---------------------------------------------------------------------------------------------
// Missatges signats
// ---------------------------------------------------------------------------------------------

/** El missatge que se signa amb `PIN_LOOKUP_KEY` per obtenir el lookup d'un PIN: `"lookup:" ‖ PIN`. */
export function pinLookupMessage(pin: string): string {
  return `lookup:${pin}`;
}

/** El missatge que se signa amb `PIN_ACCOUNT_KEY` per obtenir la contrasenya del compte: `"acct:" ‖ llavor`. */
export function accountPasswordMessage(seedHex: string): string {
  return `acct:${seedHex}`;
}

/** El missatge que se signa amb `DEVICE_KEY` per obtenir el resum d'un codi d'enrolament: `"enrol:" ‖ codi`. */
export function enrolCodeMessage(code: string): string {
  return `enrol:${code}`;
}

// ---------------------------------------------------------------------------------------------
// Comptes sintètics
// ---------------------------------------------------------------------------------------------

/** El domini del correu opac. `.invalid` no es pot lliurar mai (RFC 2606): cap correu no hi arriba. */
export const SYNTHETIC_EMAIL_DOMAIN = 'pins.obrava.invalid';

/** Caràcters hexadecimals de la llavor d'un compte (128 bits). */
export const ACCOUNT_SEED_HEX_LENGTH = 32;
/** Caràcters hexadecimals de la part aleatòria del correu opac (128 bits). */
export const SYNTHETIC_EMAIL_HEX_LENGTH = 32;
/** Caràcters hexadecimals d'un resum HMAC-SHA256 sencer. */
export const HMAC_HEX_LENGTH = 64;

const SYNTHETIC_EMAIL = /^pin-[0-9a-f]{32}@pins\.obrava\.invalid$/;
const LOWER_HEX_32 = /^[0-9a-f]{32}$/;
const LOWER_HEX_64 = /^[0-9a-f]{64}$/;

/** El correu opac d'un compte de PIN: `pin-<128 bits en hexadecimal>@pins.obrava.invalid`. */
export function syntheticEmailFromRandom(randomHex: string): string {
  if (!LOWER_HEX_32.test(randomHex)) {
    throw new RangeError('syntheticEmailFromRandom: random must be 32 lowercase hex characters');
  }
  return `pin-${randomHex}@${SYNTHETIC_EMAIL_DOMAIN}`;
}

/** `true` si `email` té exactament la forma d'un correu opac (és el mateix patró que la restricció de `user_pins`). */
export function isSyntheticEmail(email: string): boolean {
  return SYNTHETIC_EMAIL.test(email);
}

/** `true` si `text` és una llavor de compte vàlida (32 hexadecimals en minúscula). */
export function isAccountSeed(text: string): boolean {
  return LOWER_HEX_32.test(text);
}

/** `true` si `text` és un resum HMAC-SHA256 en hexadecimal minúscula (64 caràcters). */
export function isHmacHex(text: string): boolean {
  return LOWER_HEX_64.test(text);
}

// ---------------------------------------------------------------------------------------------
// Codi d'enrolament d'un aparell
// ---------------------------------------------------------------------------------------------

/**
 * El codi d'enrolament (el «PIN d'enllaç» de la tauleta) té 7 caràcters de l'alfabet del PIN: un PREFIX de 3 (15 bits;
 * troba la fila del codi viu) i un SECRET de 4 (20 bits). D'un sol ús, caduca en 24 h com a màxim (60 min per defecte);
 * 5 secrets erronis contra un prefix el cremen, així que a qui encerta el prefix només li queden 5 intents sobre 2^20
 * secrets. El prefix és únic només entre els codis VIUS (hi ha 32.768 prefixos possibles) i un obrador no en pot tenir
 * més de 100 de vius. Cost assumit: ≈ 164.000 peticions anònimes cremen tots els codis vius d'un cop (només bloqueja
 * enrolar tauletes noves). Eren 16 (8 + 8) fins al 16/10/2026.
 */
export const ENROL_CODE_LENGTH = 7;
export const ENROL_PREFIX_LENGTH = 3;

/** Els separadors que `normalizeEnrolCode` treu: espais de qualsevol mena i tots els guionets. */
const ENROL_SEPARATORS = /[\s\u2010-\u2015-]+/g;

/** El codi tal com es compara: sense espais ni guionets i en majúscula (només les lletres ASCII). */
export function normalizeEnrolCode(raw: string): string {
  return raw.replace(ENROL_SEPARATORS, '').replace(/[a-z]+/g, (letters) => letters.toUpperCase());
}

/** `true` si `code` té la forma d'un codi: exactament {@link ENROL_CODE_LENGTH} caràcters de l'alfabet. */
export function isValidEnrolCode(code: string): boolean {
  if (code.length !== ENROL_CODE_LENGTH) return false;
  for (const symbol of code) {
    if (!PIN_ALPHABET.includes(symbol)) return false;
  }
  return true;
}

/**
 * Parteix un codi vàlid en prefix i secret.
 *
 * @throws RangeError si el codi no és vàlid (no es parteix mai un text qualsevol).
 */
export function splitEnrolCode(code: string): { readonly prefix: string; readonly secret: string } {
  if (!isValidEnrolCode(code)) {
    throw new RangeError('splitEnrolCode: not a valid enrolment code');
  }
  return { prefix: code.slice(0, ENROL_PREFIX_LENGTH), secret: code.slice(ENROL_PREFIX_LENGTH) };
}

/**
 * Un codi nou a partir de bytes aleatoris que posa qui crida: els {@link ENROL_CODE_LENGTH} primers, un per
 * símbol, amb `byte & 31` (sense biaix: 256 és múltiple de 32, com al PIN). Els bytes han de venir d'un
 * generador criptogràfic.
 *
 * @throws RangeError si hi ha menys bytes dels que cal.
 */
export function generateEnrolCode(randomBytes: Uint8Array): string {
  if (randomBytes.length < ENROL_CODE_LENGTH) {
    throw new RangeError(
      `generateEnrolCode: need at least ${String(ENROL_CODE_LENGTH)} random bytes, got ${String(randomBytes.length)}`,
    );
  }
  let code = '';
  for (let i = 0; i < ENROL_CODE_LENGTH; i += 1) {
    code += PIN_ALPHABET.charAt((randomBytes[i] ?? 0) & 31);
  }
  return code;
}

/** Quants caràcters té el primer grup d'un codi quan s'ensenya («ABC-DEFG»): tres, i el segon en porta la resta (quatre). */
const ENROL_DISPLAY_GROUP = 3;

/**
 * El codi com es llegeix a la pantalla i es teclegeix a la tauleta: un grup de tres i un de quatre, «ABC-DEFG» (com el
 * PIN). Un text de tres caràcters o menys no té guionet (mai en queda un de penjat al final) i un de més llarg no es retalla.
 */
export function formatEnrolCodeForDisplay(code: string): string {
  const normalized = normalizeEnrolCode(code);
  if (normalized.length <= ENROL_DISPLAY_GROUP) return normalized;
  return `${normalized.slice(0, ENROL_DISPLAY_GROUP)}-${normalized.slice(ENROL_DISPLAY_GROUP)}`;
}
